import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppFrame, TopBar, BottomNav } from "@/components/Shell";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Loader2, ShoppingBag } from "lucide-react";
import SellDialog from "@/components/SellDialog";

const PAGE_SIZE = 50;

export default function ManageProducts() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [selling, setSelling] = useState(null); // product being sold

  // Debounce the search query
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async (reset = false) => {
    const skip = reset ? 0 : products.length;
    reset ? setLoading(true) : setLoadingMore(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (qDebounced) params.q = qDebounced;
      const [list, count] = await Promise.all([
        api.get("/products", { params }),
        skip === 0 ? api.get("/products-count", { params: qDebounced ? { q: qDebounced } : {} }) : Promise.resolve(null),
      ]);
      setProducts((prev) => (reset ? list.data : [...prev, ...list.data]));
      if (count) setTotal(count.data.total);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [qDebounced, products.length]);

  // Reset on debounced-query change
  useEffect(() => {
    load(true);
    // eslint-disable-next-line
  }, [qDebounced]);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setTotal((t) => Math.max(0, t - 1));
  };

  const hasMore = products.length < total;

  return (
    <AppFrame>
      <TopBar
        title="Products"
        subtitle="Manage"
        right={
          <button
            data-testid="add-product-header-btn"
            onClick={() => nav("/admin/add")}
            className="w-9 h-9 rounded-full grid place-items-center press"
            style={{ background: "var(--ink)", color: "var(--bg)" }}
          >
            <Plus size={16} strokeWidth={1.8} />
          </button>
        }
      />

      <div className="px-5 mt-4">
        <input
          data-testid="manage-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search all products…"
          className="w-full h-11 rounded-full border px-4 text-sm outline-none"
          style={{ borderColor: "rgba(27,25,23,0.15)", background: "var(--paper)" }}
        />
        <div className="mt-2 flex items-center justify-between text-[10px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>
          <span data-testid="products-count-label">
            {loading ? "Loading…" : `${products.length} of ${total}`}
          </span>
          {qDebounced && (
            <button
              data-testid="clear-search-btn"
              onClick={() => setQ("")}
              className="underline underline-offset-[6px]"
              style={{ textDecorationColor: "var(--accent)" }}
            >
              Clear search
            </button>
          )}
        </div>
      </div>

      <div className="px-5 mt-4 space-y-3">
        {loading ? (
          <div className="py-10 grid place-items-center" style={{ color: "var(--ink-3)" }}>
            <Loader2 className="animate-spin" size={20} />
          </div>
        ) : products.length === 0 ? (
          <div
            className="rounded-2xl py-14 text-center"
            style={{ background: "var(--bg-alt)", border: "1px solid rgba(27,25,23,0.06)" }}
          >
            <div className="font-display-italic text-xl">nothing here yet</div>
            <p className="text-xs mt-2 mb-4" style={{ color: "var(--ink-2)" }}>
              {qDebounced ? "Try a different keyword." : "Add your first product with AI."}
            </p>
            {!qDebounced && (
              <Link
                data-testid="empty-add-btn"
                to="/admin/add"
                className="inline-block text-[11px] tracking-editorial-sm uppercase px-5 py-2.5 rounded-full press"
                style={{ background: "var(--ink)", color: "var(--bg)" }}
              >
                Add product
              </Link>
            )}
          </div>
        ) : (
          <>
            {products.map((p) => (
              <div
                key={p.id}
                data-testid={`manage-product-${p.id}`}
                className="flex gap-3 items-center rounded-2xl p-3"
                style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.06)" }}
              >
                <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0" style={{ background: "#F1E8D8" }}>
                  {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] tracking-editorial-sm uppercase flex items-center gap-1" style={{ color: "var(--ink-3)" }}>
                    <span>{p.brand || p.category_name || "—"}</span>
                    {p.status === "draft" && (
                      <span className="text-[9px] tracking-editorial-sm uppercase px-1.5 py-0.5 rounded-full" style={{ background: "var(--bg-alt)", color: "var(--ink-2)" }}>
                        Draft
                      </span>
                    )}
                  </div>
                  <div className="font-display text-[15px] leading-tight truncate" style={{ color: "var(--ink)" }}>{p.name}</div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--ink-2)" }}>
                    <span className="font-display-italic" style={{ color: "var(--accent)" }}>₹{Math.round(p.selling_price)}</span>
                    {p.mrp ? <span className="line-through ml-2" style={{ color: "var(--ink-3)" }}>₹{Math.round(p.mrp)}</span> : null}
                    {typeof p.stock_quantity === "number" && p.stock_quantity > 0 && p.stock_quantity <= 5 && (
                      <span className="ml-2 text-[9px] tracking-editorial-sm uppercase px-1.5 py-0.5 rounded-full chip-accent">
                        {p.stock_quantity} left
                      </span>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`sell-${p.id}`}
                  onClick={() => setSelling(p)}
                  className="w-9 h-9 rounded-full grid place-items-center press"
                  style={{ background: "var(--accent)", color: "var(--bg)" }}
                  aria-label="Record sale"
                >
                  <ShoppingBag size={14} strokeWidth={1.6} />
                </button>
                <button
                  data-testid={`edit-${p.id}`}
                  onClick={() => nav(`/admin/products/${p.id}`)}
                  className="w-9 h-9 rounded-full grid place-items-center press"
                  style={{ border: "1px solid rgba(27,25,23,0.12)", color: "var(--ink)" }}
                >
                  <Pencil size={14} strokeWidth={1.6} />
                </button>
                <button
                  data-testid={`del-${p.id}`}
                  onClick={() => del(p.id)}
                  className="w-9 h-9 rounded-full grid place-items-center press"
                  style={{ border: "1px solid rgba(27,25,23,0.12)", color: "var(--accent)" }}
                >
                  <Trash2 size={14} strokeWidth={1.6} />
                </button>
              </div>
            ))}

            {hasMore && (
              <button
                data-testid="load-more-btn"
                onClick={() => load(false)}
                disabled={loadingMore}
                className="w-full h-12 rounded-full text-[11px] tracking-editorial-sm uppercase press disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
                style={{ background: "var(--ink)", color: "var(--bg)" }}
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="animate-spin" size={14} /> Loading…
                  </>
                ) : (
                  <>Load next {Math.min(PAGE_SIZE, total - products.length)}</>
                )}
              </button>
            )}
            {!hasMore && products.length > PAGE_SIZE && (
              <div className="text-center text-[10px] tracking-editorial-sm uppercase pt-2" style={{ color: "var(--ink-3)" }}>
                — end of catalogue —
              </div>
            )}
          </>
        )}
      </div>
      <SellDialog
        product={selling}
        onClose={() => setSelling(null)}
        onSold={(res) => {
          setSelling(null);
          // Reflect new stock in the row without a full refetch
          if (res?.sale?.product_id && res?.new_stock !== null) {
            setProducts((prev) =>
              prev.map((x) => (x.id === res.sale.product_id ? { ...x, stock_quantity: res.new_stock, availability: res.new_stock === 0 ? "out_of_stock" : x.availability } : x)),
            );
          }
        }}
      />
      <BottomNav />
    </AppFrame>
  );
}
